import {
  DrugItem,
  PackType,
  LocalStorageCartItem,
  CartItem
} from 'src/api/types';
import { isClientSide } from 'src/utils/constants';
import { zustandCartStorageName } from 'src/utils/cookieHelper';
import { pushFbEvent, FbEvents } from 'src/utils/tracking';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import postEventAddToCart from 'src/api/requests/postEventAddToCart';
import useStore from './useStore';
import { GTAGPrefix } from './useGTAG';

// Generate name with cookie prefix to keep local storage and cookies versions up-to-date

type CartDataType = {
  cartItems: CartItem[];
  isLoading: boolean;
};
interface CartState {
  cartData: CartDataType;
  simpleCartItems: LocalStorageCartItem[];
  simpleCartItem: LocalStorageCartItem;
  addedDrugsCounter: number;
  drugInCart: (translitName: string) => boolean;
  addItemToCart: (drugProps: {
    drug: DrugItem;
    packQuantity: number;
    unitQuantity: number;
    packType?: PackType;
    gtagPrefix?: GTAGPrefix;
  }) => Promise<void>;
  addItemToSingleDrugCart: (drugProps: {
    drug: DrugItem;
    packQuantity: number;
    unitQuantity: number;
    packType?: PackType;
    gtagPrefix?: GTAGPrefix;
  }) => Promise<void>;
  removeItemFromCart: (translitNames: string[]) => void;
  cleanCart: () => void;
  cleanSingleDrugCart: () => void;
  cleanDrugsCounter: () => void;
  setCartDrugsData: (drugs: DrugItem[]) => void;
}

const prepareCartItems = (
  currentSimpleCartItems: LocalStorageCartItem[],
  drugProps: {
    drug: DrugItem;
    packQuantity: number;
    unitQuantity: number;
    packType?: PackType;
    gtagPrefix?: GTAGPrefix;
  }
) => {
  const { drug, packQuantity, unitQuantity, packType, gtagPrefix } = drugProps;
  if (isClientSide) {
    let newItems = [...currentSimpleCartItems];
    const drugInCartIndex = currentSimpleCartItems.findIndex(
      (cartDrug) => cartDrug.translitName === drug.translitName
    );
    const postEventAddToCartParams = {
      translitName: drug.translitName,
      unitQuantity,
      packQuantity
    };
    // Drug already in the cart
    if (drugInCartIndex !== -1) {
      const {
        packQuantity: currentPackQuantity,
        unitQuantity: currentUnitQuantity
      } = newItems[drugInCartIndex] ?? {
        currentPackQuantity: null,
        currentUnitQuantity: null
      };
      if (
        (currentPackQuantity && currentPackQuantity < packQuantity) ||
        (currentUnitQuantity && currentUnitQuantity < unitQuantity)
      ) {
        // Quantity incrased. Track FB add to cart event
        postEventAddToCart(postEventAddToCartParams);
      }
      newItems[drugInCartIndex] = {
        ...newItems[drugInCartIndex],
        packQuantity: packQuantity || 0,
        unitQuantity: unitQuantity || 0,
        packType: packType || PackType.PACK
      };
    } else {
      newItems = [
        ...newItems,
        {
          translitName: drug.translitName,
          packQuantity: packQuantity || 0,
          unitQuantity: unitQuantity || 0,
          packType: packType || PackType.PACK,
          quantityUnitInPack: drug.quantityUnitInPack,
          gtagPrefix: gtagPrefix || GTAGPrefix.default,
          name: drug.name,
          analytics: drug.analytics
        }
      ];
      postEventAddToCart(postEventAddToCartParams);
    }
    pushFbEvent(FbEvents.AddToCart);
    return newItems;
  }
};

const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      drugInCart: (translitName: string): boolean => {
        const items = get().simpleCartItems;
        return items
          ? items.findIndex(
              (simpleCartItem) => simpleCartItem?.translitName === translitName
            ) !== -1
          : false;
      },
      cleanDrugsCounter: () =>
        set(() => {
          if (isClientSide) {
            return { addedDrugsCounter: 0 };
          }
        }),
      addedDrugsCounter: 0,
      simpleCartItems: [],
      simpleCartItem: null,
      addItemToCart: async (drugProps: {
        drug: DrugItem;
        packQuantity: number;
        unitQuantity: number;
        packType?: PackType;
        gtagPrefix?: GTAGPrefix;
      }) => {
        set((state) => {
          const currentSimpleCartItems = state?.simpleCartItems ?? [];
          const simpleCartItems = prepareCartItems(
            currentSimpleCartItems,
            drugProps
          );
          return {
            simpleCartItems,
            addedDrugsCounter: state.addedDrugsCounter + 1
          };
        });
      },
      addItemToSingleDrugCart: async (drugProps: {
        drug: DrugItem;
        packQuantity: number;
        unitQuantity: number;
        packType?: PackType;
        gtagPrefix?: GTAGPrefix;
      }) => {
        set(() => {
          const simpleCartItem = prepareCartItems([], drugProps)[0];
          return {
            simpleCartItem
          };
        });
      },
      removeItemFromCart: (translitNames: string[]) =>
        set((state) => {
          if (isClientSide) {
            const newItems = state.simpleCartItems?.filter(
              (cartDrug) => !translitNames?.includes(cartDrug.translitName)
            );
            return { simpleCartItems: newItems };
          }
        }),
      cleanCart: () =>
        set(() => {
          return {
            cartData: {
              cartItems: [],
              isLoading: false
            }
          };
        }),
      cleanSingleDrugCart: () =>
        set(() => {
          return { simpleCartItem: null };
        }),
      cartData: {
        cartItems: [],
        isLoading: false
      },
      setCartDrugsData: (drugs: DrugItem[]) => {
        set((state) => ({ cartData: { ...state.cartData, isLoading: true } }));
        set((state) => {
          const simpleCartItemList = state.simpleCartItem
            ? [state.simpleCartItem]
            : state.simpleCartItems;
          const newItems =
            (simpleCartItemList?.length !== 0 &&
              drugs?.map((drugItem) => {
                const relatedElementInLocalStorage = simpleCartItemList?.find(
                  (cartItem) => cartItem.translitName === drugItem.translitName
                ) || {
                  unitQuantity: null,
                  packQuantity: null,
                  quantityUnitInPack: null,
                  packType: null
                };
                return {
                  unitQuantity: relatedElementInLocalStorage.unitQuantity,
                  packQuantity: relatedElementInLocalStorage.packQuantity,
                  quantityUnitInPack:
                    relatedElementInLocalStorage.quantityUnitInPack,
                  drug: drugItem,
                  packType: relatedElementInLocalStorage.packType
                };
              })) ||
            [];
          return {
            cartData: { cartItems: newItems, isLoading: false }
          };
        });
      }
    }),
    {
      name: zustandCartStorageName
    }
  )
);

const fallbackStore: CartState = {
  drugInCart: () => null,
  addItemToCart: () => new Promise((resolve) => resolve(null)),
  addItemToSingleDrugCart: () => new Promise((resolve) => resolve(null)),
  removeItemFromCart: () => null,
  cleanCart: () => null,
  cleanSingleDrugCart: () => null,
  cleanDrugsCounter: () => null,
  cartData: {
    cartItems: [],
    isLoading: false
  },
  setCartDrugsData: () => null,
  simpleCartItems: [],
  simpleCartItem: null,
  addedDrugsCounter: 0
};

export default () =>
  useStore<CartState>(useCartStore, (state) => state) ?? fallbackStore;
